import { Injectable } from '@angular/core';
import { Path } from '@s8l/client-tree-lib';

import { AggregationGroup, StatsServerService } from './jsonrpc/stats-server.service';

export enum AnalyticsMetric {
  users = 'users',
  time = 'time',
  active_users = 'active_users',
  media_plays = 'media_plays',
  games_played = 'games_played',
  transition = 'transition',
  session_times = 'session_times',
  active_threshold = 'active_threshold'
}

export const MetricAttrMap = {
  [AnalyticsMetric.users]: 'users',
  [AnalyticsMetric.time]: 'time',
  [AnalyticsMetric.active_users]: 'users',
  [AnalyticsMetric.media_plays]: 'plays',
  [AnalyticsMetric.games_played]: 'plays',
  [AnalyticsMetric.session_times]: 'time',
  [AnalyticsMetric.active_threshold]: 'users'
};

@Injectable({
  providedIn: 'root'
})
export class StatsService {
  private cache = {};

  constructor(private stats: StatsServerService) {}

  fetchMetrics(
    metric: AnalyticsMetric,
    relative?: boolean,
    groupBy?: any,
    filter?: { [index in AggregationGroup]: any },
    exclude?: { [index in AggregationGroup]: any },
    from?: Date,
    to?: Date
  ): Promise<any[]> {
    const payload: any = {
      relative,
      groupBy,
      filter,
      exclude,
      from,
      to
    };

    const cacheKey = metric + '::' + JSON.stringify(payload);
    if (this.cache[cacheKey]) {
      return this.cache[cacheKey];
    }

    switch (metric) {
      case AnalyticsMetric.users:
        return (this.cache[cacheKey] = this.stats.users(payload));

      case AnalyticsMetric.active_users:
        return (this.cache[cacheKey] = this.stats.activeUsers(payload));

      case AnalyticsMetric.time:
        return (this.cache[cacheKey] = this.stats.times(payload));

      case AnalyticsMetric.media_plays:
        return (this.cache[cacheKey] = this.stats.mediaPlays(payload));

      case AnalyticsMetric.games_played:
        return (this.cache[cacheKey] = this.stats.gamesPlayed(payload));

      case AnalyticsMetric.transition:
        return (this.cache[cacheKey] = this.stats.transition(payload));

      case AnalyticsMetric.session_times:
        return (this.cache[cacheKey] = this.stats.sessionTimes(payload));

      case AnalyticsMetric.active_threshold:
        return (this.cache[cacheKey] = this.stats.activeThreshold(payload));
    }
  }

  public filterNullColumn(data: any[], groupBy: any): any[] {
    return groupBy != null ? data.filter(d => !this.isNullOrEmpty(d[groupBy])) : data;
  }

  private isNullOrEmpty(obj: any): boolean {
    return obj == null || obj === '' || (Array.isArray(obj) && obj.length == 0);
  }

  public processMetrics(
    data: any[],
    metric: AnalyticsMetric | string,
    groupBy: any,
    datasets: AggregationGroup[],
    labelNames: string
  ): { rows: string[]; columns: string[]; values: any } {
    const getLabel = val => {
      const label = [];
      for (const group of groupBy) {
        let value = this.formatGroup(group, val[group]);
        if (!value || value === '') {
          continue;
        }
        const idx = value.indexOf('/');
        if (idx !== -1) {
          const parts = value.split('/');
          value = parts[parts.length - 1];
        }
        label.push(labelNames?.replace('{value}', value) ?? value);
      }
      return label;
    };

    const values: any = {};
    const rows = {};
    const groupings = datasets.length ? datasets : [null];

    for (const val of data) {
      for (const set of groupings) {
        const dataset = set ? val[set] : metric;

        const label = getLabel(val).join(', ');
        if (label === '' || dataset === '') {
          continue;
        }

        const value = values[label] && values[label][dataset] ? values[label][dataset] + val[metric] : val[metric];
        values[label] = { ...values[label], [dataset]: value };
        rows[dataset] = true;
      }
    }

    return {
      rows: Object.keys(rows),
      columns: Object.keys(values),
      values
    };
  }

  public formatMetric(metric: AnalyticsMetric, val: any, percentage = false, def = ''): string {
    if (!val) {
      return def;
    }

    if (percentage && typeof val === 'number') {
      return (val * 100.0).toFixed(1).replace(/\.0+$/, '') + '%';
    }

    switch (MetricAttrMap[metric]) {
      case 'time':
        let h: any = Math.floor((val as number) / 1000 / 60 / 60);
        let m: any = Math.floor(((val as number) / 1000 / 60 / 60 - h) * 60);
        let s: any = Math.floor((((val as number) / 1000 / 60 / 60 - h) * 60 - m) * 60);
        s < 10 ? (s = `0${s}`) : (s = `${s}`);
        m < 10 ? (m = `0${m}`) : (m = `${m}`);
        h < 10 ? (h = `0${h}`) : (h = `${h}`);
        const r = `${h}:${m}:${s} h`;
        return r;
      default:
        return val as string;
    }
  }

  formatGroup(group: AggregationGroup, val) {
    if (!val) {
      return '';
    }

    switch (group) {
      case AggregationGroup.path:
        const p = Path.parse(val);
        return p.get().slice(-2).join('/');
      default:
        return val;
    }
  }
}
